import type { StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement, StripeElementLocale, StripeElementStyle } from "@stripe/stripe-js";



import { Format } from "@/data/util/Format";
import { JavascriptLoader } from "@/data/util/JavascriptLoader";



import { clientLocale } from "@/shared/localization/language";
import { FloatLabel } from "@/shared/ui/FloatLabel";



import { trans } from "@/app/localization/Trans";


export interface IStripeFormValues {
    cardHolder: string;
    cardnoElement: StripeCardNumberElement; // Fixed to match specific type
}

export class StripeCardForm {
    private $element: JQuery;
    private $cardHolderField: JQuery;

    private $cardnoField: JQuery;
    private $brandIcon: JQuery;
    private cardnoElement?: StripeCardNumberElement;

    private $expiryField: JQuery;
    private expiryElement?: StripeCardExpiryElement; // Fixed Type

    private $cvcField: JQuery;
    private cvcElement?: StripeCardCvcElement; // Fixed Type

    public currentBrand?: string;

    constructor() {
        this.$element = $('<div class="stripecardform form scrollable" />');

        let row = $('<div class="formrow" />').appendTo(this.$element);
        const holderCtr = $('<div class="formcontrol"><label>' + trans.creditCard.ccHolder + "</label></div>").appendTo(
            row
        );
        this.$cardHolderField = $('<input type="text" name="cardholder" />').appendTo(holderCtr);
        this.$cardHolderField.bind("change", () => {
            if (this.$cardHolderField.val().length === 0) this.$cardHolderField.parent().addClass("has-error");
            else this.$cardHolderField.parent().removeClass("has-error");
        });

        FloatLabel.bindCtrl(holderCtr);

        row = $('<div class="formrow" />').appendTo(this.$element);
        let ctrl = $(
            '<div class="formcontrol cardnumber"><label>' + trans.creditCard.ccNumber + "</label></div>"
        ).appendTo(row);
        const inner = $('<div class="inner" />').appendTo(ctrl);
        this.$brandIcon = $('<div class="brand" />').appendTo(inner);
        this.setBrandIcon();

        this.$cardnoField = $('<div class="container" />').appendTo(inner);

        row = $('<div class="formrow splitted" />').appendTo(this.$element);
        let col = $('<div class="col" />').appendTo(row);

        ctrl = $('<div class="formcontrol"><label>' + trans.creditCard.ccExpiry + "</label></div>").appendTo(col);
        this.$expiryField = $('<div class="container" />').appendTo(ctrl);

        col = $('<div class="col" />').appendTo(row);
        ctrl = $('<div class="formcontrol"><label>' + trans.creditCard.ccCvc + "</label></div>").appendTo(col);
        this.$cvcField = $('<div class="container" />').appendTo(ctrl);
    }

    public validate(): boolean {
        let isValid = true;

        if (this.$cardHolderField.val().length === 0) {
            this.$cardHolderField.parent().addClass("has-error");
            isValid = false;
        } else {
            this.$cardHolderField.parent().removeClass("has-error");
        }

        if (
            this.$cardnoField.hasClass("StripeElement--invalid") ||
            this.$cardnoField.hasClass("StripeElement--empty")
        ) {
            this.$cardnoField.parent().parent().addClass("has-error");
            isValid = false;
        }

        if (
            this.$expiryField.hasClass("StripeElement--invalid") ||
            this.$expiryField.hasClass("StripeElement--empty")
        ) {
            this.$expiryField.parent().addClass("has-error");
            isValid = false;
        }

        if (this.$cvcField.hasClass("StripeElement--invalid") || this.$cvcField.hasClass("StripeElement--empty")) {
            this.$cvcField.parent().addClass("has-error");
            isValid = false;
        }

        if (!isValid) return false;

        if (this.$element.find(".has-error").length !== 0) return false;

        return true;
    }

    public renderTo($element: JQuery): StripeCardForm {
        this.$element.appendTo($element);

        if (this.cardnoElement === undefined) {
            const elements = JavascriptLoader.stripeInstance.elements({
                fonts: [
                    {
                        family: '"Open Sans"',
                        cssSrc: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300&display=swap",
                    },
                ],
                locale: clientLocale() as StripeElementLocale, // <-- Cast it here
            });

            const baseStyle: StripeElementStyle = {
                base: {
                    lineHeight: "36px",
                    fontSize: "16px",
                    letterSpacing: "1px",
                    fontWeight: "300",
                    fontFamily: "Open Sans",
                    color: "#484848",
                },
                invalid: {
                    lineHeight: "36px",
                    fontSize: "16px",
                    letterSpacing: "1px",
                    fontWeight: "300",
                    fontFamily: "Open Sans",
                    color: "#ff0000",
                },
            };

            this.cardnoElement = elements.create("cardNumber", {
                iconStyle: "solid",
                placeholder: "",
                style: { base: baseStyle.base, invalid: baseStyle.invalid },
            });

            this.mountStripeCardNumberElementElement(this.cardnoElement, this.$cardnoField);
            this.cardnoElement!.on("change", (event) => {
                if (event!.brand) {
                    this.setBrandIcon(event!.brand);
                    this.currentBrand = event!.brand;
                }
            });

            this.expiryElement = elements.create("cardExpiry", {
                placeholder: "",
                style: { base: baseStyle.base, invalid: baseStyle.invalid },
            });
            this.mountStripeCardExpiryElementElement(this.expiryElement, this.$expiryField);

            this.cvcElement = elements.create("cardCvc", {
                placeholder: "",
                style: { base: baseStyle.base, invalid: baseStyle.invalid },
            });
            this.mountStripeCardCvcElementElement(this.cvcElement, this.$cvcField);
        }

        return this;
    }

    public destroy() {
        if (this.cardnoElement !== undefined) {
            this.cardnoElement.destroy();
            this.cvcElement!.destroy();
            this.expiryElement!.destroy();

            this.cardnoElement = undefined;
            this.cvcElement = undefined;
            this.expiryElement = undefined;
        }
    }

    public getValues(): IStripeFormValues {
        return {
            cardHolder: this.$cardHolderField.val(),
            cardnoElement: this.cardnoElement!,
        };
    }

    // Fixed parameter type to dynamically accept any of the 3 separate split element components
    private mountStripeCardNumberElementElement(element: StripeCardNumberElement, $toContainer: JQuery) {
        element.mount($toContainer[0]);
        element.on("focus", () => $toContainer.parents(".formcontrol").addClass("editing"));
        element.on("blur", () => {
            const parent = $toContainer.parents(".formcontrol");
            parent.removeClass("editing");
            if ($toContainer.hasClass("StripeElement--empty")) parent.removeClass("edited");
            else parent.addClass("edited");

            if ($toContainer.hasClass("StripeElement--invalid")) parent.addClass("has-error");
            else parent.removeClass("has-error");
        });

        element.on("change", (event) => {
            const parent = $toContainer.parents(".formcontrol");

            if (event!.complete === true) {
                if (event!.error !== undefined) parent.addClass("has-error");
                else parent.removeClass("has-error");
            }
        });
    }

    private mountStripeCardExpiryElementElement(element: StripeCardExpiryElement, $toContainer: JQuery) {
        element.mount($toContainer[0]);
        element.on("focus", () => $toContainer.parents(".formcontrol").addClass("editing"));
        element.on("blur", () => {
            const parent = $toContainer.parents(".formcontrol");
            parent.removeClass("editing");
            if ($toContainer.hasClass("StripeElement--empty")) parent.removeClass("edited");
            else parent.addClass("edited");

            if ($toContainer.hasClass("StripeElement--invalid")) parent.addClass("has-error");
            else parent.removeClass("has-error");
        });

        element.on("change", (event) => {
            const parent = $toContainer.parents(".formcontrol");

            if (event!.complete === true) {
                if (event!.error !== undefined) parent.addClass("has-error");
                else parent.removeClass("has-error");
            }
        });
    }

    private mountStripeCardCvcElementElement(element: StripeCardCvcElement, $toContainer: JQuery) {
        element.mount($toContainer[0]);
        element.on("focus", () => $toContainer.parents(".formcontrol").addClass("editing"));
        element.on("blur", () => {
            const parent = $toContainer.parents(".formcontrol");
            parent.removeClass("editing");
            if ($toContainer.hasClass("StripeElement--empty")) parent.removeClass("edited");
            else parent.addClass("edited");

            if ($toContainer.hasClass("StripeElement--invalid")) parent.addClass("has-error");
            else parent.removeClass("has-error");
        });

        element.on("change", (event) => {
            const parent = $toContainer.parents(".formcontrol");

            if (event!.complete === true) {
                if (event!.error !== undefined) parent.addClass("has-error");
                else parent.removeClass("has-error");
            }
        });
    }

    private setBrandIcon(brand?: string) {
        const icon = Format.cardIconByBrand(brand);
        if (icon !== undefined)
            this.$brandIcon.empty().css("background-image", "url('" + Format.cardIconByBrand(brand) + "')");
        else this.$brandIcon.removeAttr("style").html('<i class="fas fa-credit-card"></i>');
    }
}
